import { createProtocolExample } from "../core/BuildProtocol.ts";
import { getBlockDefinition } from "../core/blockRegistry.ts";
import { getSelectionBounds } from "../core/selectionClipboard.ts";
import { useEditorStore } from "../store/editorStore";
import type { ExportFormat } from "../types/editor";
import { BlockEntityEditor } from "./BlockEntityEditor.tsx";

export function InspectorPanel() {
  const revision = useEditorStore((state) => state.worldRevision);
  const world = useEditorStore((state) => state.world);
  const selectedPosition = useEditorStore(
    (state) => state.selectedPosition
  );
  const selectionEnd = useEditorStore(
    (state) => state.selectionEnd
  );
  const selectionClipboard = useEditorStore(
    (state) => state.selectionClipboard
  );
  const selectionTransformOffset = useEditorStore(
    (state) => state.selectionTransformOffset
  );
  const selectionArrayCopies = useEditorStore(
    (state) => state.selectionArrayCopies
  );
  const deleteSelected = useEditorStore(
    (state) => state.deleteSelected
  );
  const protocolInput = useEditorStore(
    (state) => state.protocolInput
  );
  const setProtocolInput = useEditorStore(
    (state) => state.setProtocolInput
  );
  const runProtocol = useEditorStore((state) => state.runProtocol);
  const exportFormat = useEditorStore(
    (state) => state.exportFormat
  );
  const exportOrigin = useEditorStore(
    (state) => state.exportOrigin
  );
  const exportOutput = useEditorStore(
    (state) => state.exportOutput
  );
  const exportCommandCount = useEditorStore(
    (state) => state.exportCommandCount
  );

  void revision;

  const selectedBlock = selectedPosition
    ? world.getBlockData(
        selectedPosition.x,
        selectedPosition.y,
        selectedPosition.z
      )
    : null;
  const definition = selectedBlock
    ? getBlockDefinition(selectedBlock.blockId)
    : null;
  const selectionBounds = selectedPosition
    ? getSelectionBounds(
        selectedPosition,
        selectionEnd || selectedPosition
      )
    : null;
  const isRegionSelection =
    Boolean(selectionBounds && selectionBounds.volume > 1);

  const copyExport = async () => {
    await navigator.clipboard.writeText(exportOutput);
    useEditorStore.getState().notify("Export copied");
  };

  return (
    <aside className="tool-panel right-panel">
      <section className="panel-section">
        <div className="panel-heading">
          <span>Selection</span>
          <span className="panel-badge">
            {selectionBounds
              ? (
                  isRegionSelection
                    ? selectionBounds.volume.toLocaleString() + " voxels"
                    : definition?.shortName ?? "1 voxel"
                )
              : "None"}
          </span>
        </div>
        <div className="selection-card">
          <div className="selection-coordinate-row">
            {(["x", "y", "z"] as const).map((axis) => (
              <div key={axis}>
                <span>{axis.toUpperCase()}</span>
                <strong>
                  {selectionBounds
                    ? (
                        selectionBounds.min[axis] ===
                        selectionBounds.max[axis]
                          ? selectionBounds.min[axis]
                          : selectionBounds.min[axis] +
                            " → " +
                            selectionBounds.max[axis]
                      )
                    : "—"}
                </strong>
              </div>
            ))}
          </div>

          {selectionBounds && (
            <div className="selection-meta-line">
              Size {selectionBounds.size.x} × {selectionBounds.size.y} × {selectionBounds.size.z}
            </div>
          )}

          <div className="split-actions">
            <button
              className="wide-action-button"
              type="button"
              disabled={!selectedPosition}
              onClick={() =>
                useEditorStore.getState().copySelection()
              }
            >
              Copy
            </button>
            <button
              className="wide-action-button secondary-action"
              type="button"
              disabled={!selectedPosition}
              onClick={() =>
                useEditorStore.getState().cutSelection()
              }
            >
              Cut
            </button>
          </div>
          <button
            className="wide-action-button danger-action"
            type="button"
            disabled={!selectedPosition}
            onClick={deleteSelected}
          >
            Delete selection
          </button>
        </div>

        {selectionBounds && (
          <div className="selection-card transform-card">
            <div className="panel-heading compact-heading">
              <span>Transform selection</span>
              <span className="panel-badge panel-badge-green">
                Offset
              </span>
            </div>
            <div className="transform-input-grid">
              {(["x", "y", "z"] as const).map((axis) => (
                <label key={axis}>
                  <span>{axis.toUpperCase()}</span>
                  <input
                    type="number"
                    value={selectionTransformOffset[axis]}
                    onChange={(event) =>
                      useEditorStore
                        .getState()
                        .setSelectionTransformOffset(
                          axis,
                          Number(event.target.value)
                        )
                    }
                  />
                </label>
              ))}
              <label>
                <span>Copies</span>
                <input
                  type="number"
                  min="1"
                  max="64"
                  value={selectionArrayCopies}
                  onChange={(event) =>
                    useEditorStore
                      .getState()
                      .setSelectionArrayCopies(
                        Number(event.target.value)
                      )
                  }
                />
              </label>
            </div>
            <div className="selection-meta-line">
              Offset uses Minecraft blocks. Array adds this many repeated
              copies in one undoable edit.
            </div>
            <div className="transform-action-grid">
              <button
                className="wide-action-button secondary-action"
                type="button"
                onClick={() =>
                  useEditorStore
                    .getState()
                    .moveSelectionByOffset()
                }
              >
                Move
              </button>
              <button
                className="wide-action-button secondary-action"
                type="button"
                onClick={() =>
                  useEditorStore
                    .getState()
                    .duplicateSelectionByOffset()
                }
              >
                Duplicate
              </button>
              <button
                className="wide-action-button"
                type="button"
                onClick={() =>
                  useEditorStore
                    .getState()
                    .arraySelectionByOffset()
                }
              >
                Linear array
              </button>
            </div>
          </div>
        )}

        {selectionClipboard && (
          <div className="selection-card clipboard-card">
            <div className="panel-heading compact-heading">
              <span>Internal clipboard</span>
              <span className="panel-badge panel-badge-purple">
                {selectionClipboard.voxels.length.toLocaleString()} blocks
              </span>
            </div>
            <div className="selection-meta-line">
              {selectionClipboard.size.x} × {selectionClipboard.size.y} × {selectionClipboard.size.z}
            </div>
            <div className="split-actions">
              <button
                className="wide-action-button secondary-action"
                type="button"
                onClick={() =>
                  useEditorStore.getState().rotateClipboard()
                }
              >
                Rotate 90°
              </button>
              <button
                className="wide-action-button secondary-action"
                type="button"
                onClick={() =>
                  useEditorStore.getState().mirrorClipboardX()
                }
              >
                Mirror X
              </button>
            </div>
            <div className="split-actions">
              <button
                className="wide-action-button secondary-action"
                type="button"
                onClick={() =>
                  useEditorStore.getState().mirrorClipboardZ()
                }
              >
                Mirror Z
              </button>
              <button
                className="wide-action-button"
                type="button"
                onClick={() =>
                  useEditorStore.getState().pasteClipboard()
                }
              >
                Paste @ cursor
              </button>
            </div>
          </div>
        )}

        {selectedBlock && !isRegionSelection && (
          <BlockEntityEditor />
        )}
      </section>

      <section className="panel-section">
        <div className="panel-heading">
          <span>AI Build Protocol</span>
          <span className="panel-badge panel-badge-purple">
            MDP v1
          </span>
        </div>
        <textarea
          className="code-input"
          value={protocolInput}
          spellCheck={false}
          onChange={(event) =>
            setProtocolInput(event.target.value)
          }
        />
        <div className="split-actions">
          <button
            className="wide-action-button"
            type="button"
            onClick={runProtocol}
          >
            Run protocol
          </button>
          <button
            className="wide-action-button secondary-action"
            type="button"
            onClick={() =>
              setProtocolInput(
                JSON.stringify(createProtocolExample(), null, 2)
              )
            }
          >
            Example
          </button>
        </div>
      </section>

      <section className="panel-section">
        <div className="panel-heading">
          <span>Minecraft export</span>
          <span className="panel-badge panel-badge-green">
            {exportCommandCount.toLocaleString()} commands
          </span>
        </div>
        <div className="origin-grid">
          {(["x", "y", "z"] as const).map((axis) => (
            <label key={axis}>
              {"Origin " + axis.toUpperCase()}
              <input
                type="number"
                value={exportOrigin[axis]}
                onChange={(event) =>
                  useEditorStore
                    .getState()
                    .setExportOrigin(
                      axis,
                      Number(event.target.value)
                    )
                }
              />
            </label>
          ))}
        </div>
        <select
          className="select-control"
          value={exportFormat}
          onChange={(event) =>
            useEditorStore
              .getState()
              .setExportFormat(
                event.target.value as ExportFormat
              )
          }
        >
          <option value="mcfunction">Java .mcfunction</option>
          <option value="commands">Chat /fill commands</option>
          <option value="skript">Skript command</option>
          <option value="json">Voxel project JSON</option>
        </select>
        <textarea
          className="code-output"
          readOnly
          value={exportOutput}
        />
        <div className="split-actions">
          <button
            className="wide-action-button"
            type="button"
            onClick={() =>
              useEditorStore.getState().refreshExport()
            }
          >
            Refresh
          </button>
          <button
            className="wide-action-button secondary-action"
            type="button"
            onClick={copyExport}
          >
            Copy
          </button>
        </div>
      </section>
    </aside>
  );
}
